// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailComplete from './pages/VerifyEmailComplete';
import Dashboard from './pages/Dashboard';
import Assessment from './pages/Assessment';
import Results from './pages/Results';
import TeamManagement from './pages/TeamManagement';
import TeamAssessment from './pages/TeamAssessment';
import TeamMeetings from './pages/TeamMeetings';
import TeamMeetingDetail from './pages/TeamMeetingDetail';
import AdminPanel from './pages/AdminPanel';
import ConsultantMeetings from './pages/ConsultantMeetings';
import ConsultantTeamHub from './pages/ConsultantTeamHub';
import ConsultantAssessment from './pages/ConsultantAssessment';
import NewMeeting from './pages/NewMeeting';
import RecordMeeting from './pages/RecordMeeting';
import MeetingReport from './pages/MeetingReport';
import MeetingReview from './pages/MeetingReview';
import MeetingInventory from './pages/MeetingInventory';
import PerceivedVsObserved from './pages/PerceivedVsObserved';
import Pricing from './pages/Pricing';
import WorkingGenius from './pages/WorkingGenius';
import ConsultantObservation from './pages/ConsultantObservation';
import Contact from './pages/Contact';
import AdminChartDashboard from './pages/AdminChartDashboard';
import ReportPrint from './pages/ReportPrint';
import TeamAssessmentPrint from './pages/TeamAssessmentPrint';
import DevRoleSwitcher from './components/DevRoleSwitcher';
import AdminRoleSwitcher from './components/AdminRoleSwitcher';
import ImpersonationBanner from './components/ImpersonationBanner';
import PWAInstallBanner from './components/PWAInstallBanner';
import './index.css';
import './pwa.css';

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <DevRoleSwitcher />
        <AdminRoleSwitcher />
        <ImpersonationBanner />
        <PWAInstallBanner />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email-complete" element={<VerifyEmailComplete />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />

          {/* Print routes — no auth session (Puppeteer); secured by Firestore print token */}
          <Route path="/report-print/:meetingId" element={<ReportPrint />} />
          <Route path="/team-assessment-print/:teamId" element={<TeamAssessmentPrint />} />

          {/* Protected — all authenticated users */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/assessment" element={
            <ProtectedRoute>
              <Assessment />
            </ProtectedRoute>
          } />

          <Route path="/results" element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          } />

          {/* Working Genius — team members and leaders */}
          <Route path="/working-genius" element={
            <ProtectedRoute allowedRoles={['team_member', 'team_leader', 'admin']}>
              <WorkingGenius />
            </ProtectedRoute>
          } />

          {/* Team leader and team member — meetings */}
          <Route path="/team-meetings" element={
            <ProtectedRoute allowedRoles={['team_leader', 'team_member', 'admin']}>
              <TeamMeetings />
            </ProtectedRoute>
          } />

          <Route path="/team-meetings/:meetingId" element={
            <ProtectedRoute allowedRoles={['team_leader', 'team_member', 'admin']}>
              <TeamMeetingDetail />
            </ProtectedRoute>
          } />

          {/* Consultant Observation — all team roles */}
          <Route path="/consultant-observation" element={
            <ProtectedRoute allowedRoles={['team_leader', 'team_member', 'consultant', 'admin']}>
              <ConsultantObservation />
            </ProtectedRoute>
          } />

          {/* Team leader only */}
          <Route path="/team" element={
            <ProtectedRoute allowedRoles={['team_leader', 'admin']}>
              <TeamManagement />
            </ProtectedRoute>
          } />

          <Route path="/team-assessment" element={
            <ProtectedRoute allowedRoles={['team_leader', 'team_member', 'admin']}>
              <TeamAssessment />
            </ProtectedRoute>
          } />

          {/* Perceived vs Observed — team leader + consultant */}
          <Route path="/perceived-vs-observed" element={
            <ProtectedRoute allowedRoles={['team_leader', 'consultant', 'admin']}>
              <PerceivedVsObserved />
            </ProtectedRoute>
          } />

          {/* Admin only */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPanel />
            </ProtectedRoute>
          } />

          <Route path="/admin/charts" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminChartDashboard />
            </ProtectedRoute>
          } />

          {/* Consultant — specific meeting actions first (before dynamic :teamId) */}
          <Route path="/meetings/new" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <NewMeeting />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:meetingId/record" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <RecordMeeting />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:meetingId/inventory" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <MeetingInventory />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:meetingId/review" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <MeetingReview />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:meetingId/observe" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <ConsultantAssessment />
            </ProtectedRoute>
          } />
          {/* Team selector then team hub */}
          <Route path="/meetings" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <ConsultantMeetings />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:teamId" element={
            <ProtectedRoute allowedRoles={['consultant', 'admin']}>
              <ConsultantTeamHub />
            </ProtectedRoute>
          } />
          <Route path="/meetings/:meetingId/report" element={
            <ProtectedRoute>
              <MeetingReport />
            </ProtectedRoute>
          } />

          {/* Default */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
